import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/stores/authStore';
import ForbiddenPage from '@/pages/ForbiddenPage';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string | string[];
  requireAll?: boolean;
  /** If set, redirects to this route on denial instead of rendering ForbiddenPage. */
  redirectOnDenied?: string;
}

export default function ProtectedRoute({
  children,
  requiredPermission,
  requireAll,
  redirectOnDenied,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasAnyPermission, hasAllPermissions } = usePermission();
  const authStatus = useAuthStore((s) => s.authStatus);

  // Cookie-rehydration in flight (cold load). Don't redirect yet — the boot
  // check in App.tsx may still hydrate the store from a valid httpOnly
  // session cookie. Show a spinner until bootstrap completes, then decide.
  if (authStatus === 'pending') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!requiredPermission) {
    return <>{children}</>;
  }

  const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
  const hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);

  if (!hasAccess) {
    if (redirectOnDenied) {
      return <Navigate to={redirectOnDenied} replace />;
    }
    return <ForbiddenPage missingPermissions={permissions} />;
  }

  return <>{children}</>;
}
